/**
 * Where the record came from (E4, spec D25 §9.4): the original email's
 * facts, the in-product thread one click away, and the mailbox's own copy
 * when the provider reported a link. Nothing for a record born without a
 * conversation.
 */
import { Link } from 'react-router-dom'
import { ExternalLink, MessageSquare } from 'lucide-react'
import { useRecordSource } from '@/hooks/useRecordExtras'
import { RecordSection } from './RecordSection'
import { sourceSummary } from './sectionSummaries'
import { sourceLabel, sourceRefOf, useSourceHref } from './sourceText'

export function SourceCard({ orderId, focused = false }: { orderId: string; focused?: boolean }) {
  const source = useRecordSource(orderId)
  const data = source.data
  const href = useSourceHref(sourceRefOf(data))
  if (!data || data.kind === 'none') return null
  const email = data.kind === 'email' ? data.email : null
  // SA-946: a record born from a WhatsApp group message.
  const group = data.kind === 'whatsapp-group' ? data.group ?? null : null
  return (
    <RecordSection id="source" title={sourceLabel(data.kind)} summary={sourceSummary(data)} defaultOpen={false} focused={focused}>
      {email && (
        <div className="text-sm mb-2">
          {email.subject && <p className="font-medium text-foreground">{email.subject}</p>}
          <p className="text-muted-foreground">
            {email.fromAddr ? `From ${email.fromAddr}` : 'Sender not recorded'}
            {email.receivedAt && <> · received {new Date(email.receivedAt).toLocaleString()}</>}
          </p>
        </div>
      )}
      {group && (
        <div className="text-sm mb-2">
          <p className="text-muted-foreground">
            {group.fromName ? `From ${group.fromName}` : 'Sender not recorded'}
            {group.subject && ` in ${group.subject}`}
            {group.receivedAt && <> · received {new Date(group.receivedAt).toLocaleString()}</>}
          </p>
        </div>
      )}
      <div className="flex flex-wrap gap-4 text-sm">
        {href && (
          <Link to={href} className="inline-flex items-center gap-1 text-primary hover:text-primary/80">
            <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" /> {group ? 'Open in the Inbox' : 'Open thread'}
          </Link>
        )}
        {email?.webUrl && (
          <a href={email.webUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:text-primary/80">
            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" /> Open in mailbox
          </a>
        )}
      </div>
    </RecordSection>
  )
}
